import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, clearToken, getToken } from "@/lib/api";
import { format, parseISO } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { LogOut, MoreHorizontal, RefreshCw, Loader2 } from "lucide-react";

const STATUS_OPTIONS = ["all", "pending", "confirmed", "cancelled", "completed"];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, today: 0 });
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!getToken()) { nav("/admin/login"); return; }
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => { clearToken(); nav("/admin/login"); });
  }, [nav]);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, sRes] = await Promise.all([
        api.get("/admin/appointments", { params: { status: statusFilter, date: dateFilter || undefined } }),
        api.get("/admin/stats"),
      ]);
      setItems(aRes.data);
      setStats(sRes.data);
    } catch (err) {
      if (err?.response?.status === 401) { clearToken(); nav("/admin/login"); return; }
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, dateFilter, nav]);

  useEffect(() => { if (me) load(); }, [me, load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/appointments/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const logout = () => { clearToken(); nav("/admin/login"); };

  const grouped = useMemo(() => {
    const g = {};
    for (const a of items) { (g[a.appointment_date] ??= []).push(a); }
    return Object.entries(g).sort(([a],[b]) => a.localeCompare(b));
  }, [items]);

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-stone-50">
      {/* Top bar */}
      <div className="border-b border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div>
            <div className="eyebrow">Physiofit · Admin</div>
            <h1 className="mt-1 font-heading text-2xl text-stone-900">Bookings dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={load} data-testid="refresh-btn" className="inline-flex items-center gap-2 px-3 py-2 border border-stone-200 rounded-md text-sm hover:border-emerald-900 hover:text-emerald-900 transition-colors">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={logout} data-testid="logout-btn" className="inline-flex items-center gap-2 px-3 py-2 border border-stone-200 rounded-md text-sm hover:border-emerald-900 hover:text-emerald-900 transition-colors">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { l: "Total", v: stats.total, k: "total" },
            { l: "Pending", v: stats.pending, k: "pending" },
            { l: "Confirmed", v: stats.confirmed, k: "confirmed" },
            { l: "Today", v: stats.today, k: "today" },
          ].map((s) => (
            <div key={s.l} className="border border-stone-200 bg-white p-5" data-testid={`stat-${s.k}`}>
              <div className="eyebrow">{s.l}</div>
              <div className="mt-2 font-heading text-3xl text-stone-900">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap items-end gap-4">
          <div className="min-w-[200px]">
            <div className="eyebrow">Status</div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="mt-2" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`filter-status-${s}`}>{s[0].toUpperCase()+s.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="eyebrow">Date</div>
            <input type="date" data-testid="filter-date" value={dateFilter} onChange={(e)=>setDateFilter(e.target.value)} className="mt-2 border border-stone-200 focus:border-emerald-900 outline-none px-3 py-2 bg-white rounded-md" />
          </div>
          {(dateFilter || statusFilter !== "all") && (
            <button onClick={() => { setDateFilter(""); setStatusFilter("all"); }} data-testid="clear-filters" className="px-3 py-2 border border-stone-200 rounded-md text-sm hover:border-emerald-900 hover:text-emerald-900">Clear filters</button>
          )}
        </div>

        {/* List */}
        <div className="mt-10">
          {loading ? (
            <div className="flex items-center gap-2 text-stone-500"><Loader2 className="animate-spin" size={16} /> Loading appointments...</div>
          ) : items.length === 0 ? (
            <div className="border border-stone-200 bg-white p-10 text-center text-stone-500" data-testid="empty-state">No appointments match these filters.</div>
          ) : (
            <div className="space-y-10">
              {grouped.map(([d, list]) => (
                <div key={d}>
                  <div className="flex items-center gap-3">
                    <div className="font-heading text-xl text-stone-900">{format(parseISO(d), "EEEE, d MMMM yyyy")}</div>
                    <div className="text-xs text-stone-500">{list.length} booking{list.length>1?"s":""}</div>
                  </div>
                  <div className="mt-3 border border-stone-200 bg-white overflow-x-auto">
                    <table className="w-full text-sm" data-testid={`appointments-table-${d}`}>
                      <thead>
                        <tr className="text-left text-stone-500">
                          <th className="px-4 py-3 font-medium">Time</th>
                          <th className="px-4 py-3 font-medium">Client</th>
                          <th className="px-4 py-3 font-medium">Service</th>
                          <th className="px-4 py-3 font-medium">Contact</th>
                          <th className="px-4 py-3 font-medium">Notes</th>
                          <th className="px-4 py-3 font-medium">Status</th>
                          <th className="px-4 py-3 font-medium text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.sort((a,b)=>a.appointment_time.localeCompare(b.appointment_time)).map((a) => (
                          <tr key={a.id} className="border-t border-stone-200" data-testid={`row-${a.id}`}>
                            <td className="px-4 py-3 font-mono text-stone-800">{a.appointment_time}</td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-stone-900">{a.name}</div>
                            </td>
                            <td className="px-4 py-3 text-stone-700">{a.service}</td>
                            <td className="px-4 py-3 text-stone-700">
                              <div>{a.email}</div>
                              <div className="text-stone-500">{a.phone}</div>
                            </td>
                            <td className="px-4 py-3 text-stone-600 max-w-[240px] truncate">{a.notes || "—"}</td>
                            <td className="px-4 py-3"><StatusBadge s={a.status} /></td>
                            <td className="px-4 py-3 text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button data-testid={`action-${a.id}`} className="inline-flex items-center px-2 py-1.5 border border-stone-200 rounded hover:border-emerald-900">
                                    <MoreHorizontal size={16} />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-white">
                                  <DropdownMenuLabel>Set status</DropdownMenuLabel>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => updateStatus(a.id, "confirmed")} data-testid={`set-confirmed-${a.id}`}>Confirm</DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => updateStatus(a.id, "completed")} data-testid={`set-completed-${a.id}`}>Mark completed</DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => updateStatus(a.id, "pending")} data-testid={`set-pending-${a.id}`}>Set pending</DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => updateStatus(a.id, "cancelled")} data-testid={`set-cancelled-${a.id}`} className="text-red-700">Cancel</DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

function StatusBadge({ s }) {
  const map = {
    pending:   { bg: "bg-amber-50",    text: "text-amber-800",    border: "border-amber-200" },
    confirmed: { bg: "bg-emerald-50",  text: "text-emerald-900",  border: "border-emerald-200" },
    cancelled: { bg: "bg-red-50",      text: "text-red-800",      border: "border-red-200" },
    completed: { bg: "bg-stone-100",   text: "text-stone-700",    border: "border-stone-200" },
  };
  const c = map[s] || map.pending;
  return <span className={`inline-flex items-center px-2 py-0.5 text-xs border ${c.bg} ${c.text} ${c.border} rounded-full`}>{s}</span>;
}
